import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  MutationOptions,
  Operation,
  OperationVariables,
  QueryOptions
} from '@apollo/client/core'
import 'cross-fetch/polyfill'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { BaseModelDto } from 'booksprout'
import { Loading, Notify } from 'quasar'
import * as Sentry from '@sentry/vue'
import promiseToObservable from '../_base/apollo/promiseToObservable'
import { IsReviewerApp } from '../../utils'
import { useConfig } from '../config/configModule'
import { useUi } from '../ui/uiModule'
import { useAxios } from '../axios/axiosModule'
import authStoreGetters from '../authentication/store/getters'
import { _setAuthExpired, _setCsrfToken } from '../authentication/store/mutations'
import {
  ErrorResponse,
  getConstraintErrorResponse,
  getErrorResponse,
  getErrorResponseFromCode
} from '../../base/apiErrorHandler'

/**
 * Replaces ApolloClientService and BaseApolloClientService.
 *
 * Reads auth from the authentication store and config from configModule rather than injected
 * StoreService/ConfigService, and reaches axios through `useAxios()` instead of
 * `container.get('AxiosService')` — which removes the last service-locator call outside the
 * Vuex auth actions.
 *
 * One Vuex dependency remains on purpose: the 403/404/offline paths dispatch REDIRECT_TO, which
 * writes `ui.redirect` for a component to watch. `ui` moves in stage 5; a redirect callback is
 * injected here so this module never imports Vuex itself.
 */

/**
 * What Notify.create hands back: calling it again updates the notification in place. Typed here
 * rather than carried over as the old service's bare `Function`.
 */
type NotificationUpdater = (props?: Record<string, unknown>) => void

/**
 * Set once while a connection failure is being retried, so the "Connecting …" notification is
 * not stacked. Module scope, exactly as it was in the old service.
 */
let failedToConnectNotification: NotificationUpdater | undefined

let client: ApolloClient<any> | undefined

let showGlobalLoader = true

const getGraphQLErrorPayload = (error: any): any => {
  return error?.extensions?.exception?.response ||
    error?.extensions?.response ||
    error?.extensions?.originalError ||
    error
}

/**
 * Carried over verbatim from BaseApolloClientService, branch order included. The API answers with
 * several different error shapes depending on which layer rejected.
 */
const generateErrorResponse = (error: any): ErrorResponse => {
  if (typeof error === 'string') {
    return getErrorResponse(error)
  }

  /**
   * Behaviour is identical to BaseApolloClientService's version; the redundant optional access
   * inside each branch is dropped. `error?.statusCode !== void 0` and
   * `Array.isArray(error?.message)` both already establish that `error` is a non-null object, so
   * `error?.message` inside them could never short-circuit.
   */
  const hasConstraintArray = Array.isArray(error?.message) &&
    error.message.length > 0 &&
    error.message[0]?.constraints !== void 0

  if (error?.statusCode !== void 0) {
    if (error.message?.errorCode !== void 0) {
      return getErrorResponseFromCode(error.message.errorCode)
    }

    if (typeof error.message === 'string' && error.message) {
      return getErrorResponse(error.message)
    }

    if (hasConstraintArray) {
      return getConstraintErrorResponse(error.message[0].constraints)
    }

    return getErrorResponse(error.error || error.message)
  }

  if (hasConstraintArray) {
    return getConstraintErrorResponse(error.message[0].constraints)
  }

  if (error?.message?.errorCode !== void 0) {
    return getErrorResponseFromCode(error.message.errorCode)
  }

  if (error?.message !== void 0) {
    return getErrorResponse(error.message)
  }

  return getErrorResponseFromCode('somethingWentWrong')
}

/**
 * Unwraps the single top-level field out of a GraphQL result and detaches it from the Apollo
 * cache. The detaching matters: cached values are frozen, and binding one to `v-model` throws.
 */
const handleResponse = (r: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    const errors = r?.errors || r?.error?.graphQLErrors

    if (errors && errors.length) {
      return reject(generateErrorResponse(getGraphQLErrorPayload(errors[0])))
    }

    if (r.networkError) {
      return reject(getErrorResponse(r.networkError.result.errors))
    }

    const keys = Object.keys(r.data)
    if (!r || !r.data || keys.length === 0) {
      return resolve(null)
    }

    const thisData = r.data[keys[0]]

    if (Array.isArray(thisData)) {
      // A spread would not detach the *items*, so each one is copied.
      return resolve(thisData.map(item => ({ ...item })))
    }

    if (typeof thisData === 'object') {
      if (thisData === null) {
        return resolve(null)
      }

      /**
       * stringify/parse rather than a shallow spread: nested props (user preferences, for one)
       * stay readonly otherwise. Safe because anything Apollo returned was serialisable.
       */
      return resolve(JSON.parse(JSON.stringify(thisData)))
    }

    return resolve(thisData)
  })
}

/**
 * Replaces the Vuex GET_CSRF_TOKEN action, which hand-built a StoreService, ConfigService and
 * AxiosService to make one GET. Same request, same commit, without the bypass.
 */
const refreshToken = async (): Promise<void> => {
  if (!authStoreGetters.isAuthenticated) {
    return
  }

  const r = await useAxios().axios().get('auth/getCsrfToken', { withCredentials: true })
  _setCsrfToken(r.data)
}

const authorizationHeader = () => {
  return authStoreGetters.isAuthenticated ? `Bearer ${authStoreGetters.authToken}` : ''
}

const reportToSentry = (error: any) => {
  const { graphQLErrors, networkError, operation } = error

  Sentry.withScope(scope => {
    scope.setTransactionName(operation.operationName)
    scope.setContext('apolloGraphQLOperation', {
      operationName: operation.operationName,
      variables: operation.variables,
      extensions: operation.extensions
    })

    graphQLErrors?.forEach((graphQLError: any) => {
      Sentry.captureException(graphQLError.message, {
        fingerprint: ['{{ default }}', '{{ transaction }}'],
        contexts: {
          apolloGraphQLError: {
            error: graphQLError,
            message: graphQLError.message,
            extensions: graphQLError.extensions
          }
        }
      })
    })

    if (networkError) {
      Sentry.captureException(networkError.message, {
        contexts: {
          apolloNetworkError: {
            error: networkError,
            extensions: (networkError as any).extensions
          }
        }
      })
    }
  })
}

const RECONNECT_CHECK_INTERVAL_MS = 5000
const RECONNECT_CHECK_TIMEOUT_MS = 5000

// A replayed operation that keeps hitting the network is not an outage we can wait out, so stop
// re-arming the watcher for it and let the error reach the caller.
const RECONNECT_MAX_REPLAYS = 2

// Module level so one outage means one notification and one poll, however many operations happen to
// fail while the API is away.
let reconnectPromise: Promise<void> | undefined
let reconnectInterval: ReturnType<typeof setInterval> | undefined

/**
 * Is this a write? Mutations are the operations we must not replay behind the user's back once a
 * connection comes back. [sc-27886]
 */
const isMutation = (operation: Operation): boolean =>
  operation.query?.definitions?.some(definition =>
    definition.kind === 'OperationDefinition' && definition.operation === 'mutation'
  ) === true

/**
 * Show the user we have lost the API and RESOLVE once it answers again.
 *
 * It used to reload the page instead, which threw away everything the user had typed but not saved —
 * and the mobile app hits this every time the OS freezes the WebView in the background, so readers
 * were losing half-written reviews just by switching apps. [sc-27886, carried from dev, where this
 * lived in BaseApolloClientService]
 *
 * All callers during one outage share a single notification and poll, and the state is cleared on the
 * way out so a later outage arms a fresh watcher.
 */
/**
 * Forget the current outage: stop polling and let the next failure arm a fresh watcher.
 *
 * Both the success path and `reset()` need this, and the interval handle has to live at module scope
 * for it -- a run left polling on a promise nobody is waiting for keeps every later caller attached to
 * a watcher that already resolved.
 */
const stopWaitingForConnection = () => {
  if (reconnectInterval !== void 0) {
    clearInterval(reconnectInterval)
    reconnectInterval = void 0
  }

  reconnectPromise = void 0
}

const waitForConnection = (): Promise<void> => {
  if (reconnectPromise !== void 0) {
    return reconnectPromise
  }

  failedToConnectNotification =
    Notify.create({ type: 'ongoing', message: 'Connecting ...' }) as NotificationUpdater

  reconnectPromise = new Promise<void>(resolve => {
    let reconnectCheckInFlight = false

    reconnectInterval = setInterval(() => {
      if (reconnectCheckInFlight) {
        return
      }

      reconnectCheckInFlight = true

      void useAxios().axios().post('auth/live', {}, { timeout: RECONNECT_CHECK_TIMEOUT_MS }).then(() => {
        stopWaitingForConnection()

        if (failedToConnectNotification) {
          failedToConnectNotification({
            type: 'positive',
            message: 'Connected',
            icon: 'app:check',
            timeout: 2000
          })
        }

        failedToConnectNotification = void 0
        resolve()
      }).catch(() => {
        // Keep polling until the connection check succeeds.
        reconnectCheckInFlight = false
      })
    }, RECONNECT_CHECK_INTERVAL_MS)
  })

  return reconnectPromise
}

const createErrorLink = () => onError((error: any): any => {
  const { response, networkError, operation, forward } = error

  reportToSentry(error)

  if (networkError) {
    // Mobile only; nginx answers first in a browser.
    if (networkError?.result?.offline) {
      /**
       * Redirect and stop. Returning the dispatch promise would make the error link call
       * `.subscribe()` on a non-Observable ("subscribe is not a function").
       */
      useUi().ui.redirectTo({ url: 'maintenance' })
      return
    }

    if (!networkError.result) {
      // Tell the user we have lost the API and wait for it to come back, then replay — the page and
      // everything on it stays exactly where it was. [sc-27886]
      const reconnected = waitForConnection()

      /**
       * Never replay a mutation after an outage. The server may well have processed the original
       * before the connection dropped, so re-sending it minutes later can double up on whatever it
       * was writing. Let the caller's error handling deal with it — the user is still on their form,
       * with their input intact, and can submit again.
       */
      if (isMutation(operation)) {
        return
      }

      // A replay that keeps failing must not re-arm the watcher forever.
      const replays = (operation.getContext().reconnectReplays as number) || 0
      if (replays >= RECONNECT_MAX_REPLAYS) {
        return
      }

      return promiseToObservable(reconnected).flatMap(() => {
        operation.setContext(({ headers = {} }) => ({
          reconnectReplays: replays + 1,
          headers: {
            ...headers,
            authorization: authStoreGetters.isAuthenticated ? `Bearer ${authStoreGetters.authToken}` : ''
          }
        }))

        return forward(operation)
      })
    }

    if (networkError.result.message === 'invalid csrfToken') {
      return promiseToObservable(refreshToken()).flatMap(() => {
        operation.setContext(({ headers = {} }) => ({
          headers: { ...headers, 'CSRF-Token': authStoreGetters.csrfToken }
        }))

        return forward(operation)
      })
    }

    return
  }

  if (response !== void 0) {
    const errors = response.errors

    if (errors && errors.length && errors[0].extensions) {
      const payload = getGraphQLErrorPayload(errors[0])

      if (payload !== void 0 && payload.statusCode) {
        switch (payload.statusCode) {
          case 401:
            _setAuthExpired(true)
            break
          case 403:
            useUi().ui.redirectTo({ name: 'login' })
            break
          case 404:
            useUi().ui.redirectTo({ name: '404' })
            break
        }
      }
    }
  }
})

/**
 * The auth and csrf headers, read from the store on every operation.
 *
 * They used to be static `HttpLink` headers, which are evaluated once, when the link is built. That was
 * harmless while `get client()` built a new client per access; once the client became built once and
 * reused, it froze them at whatever the store held the first time anything touched GraphQL - on a
 * fresh launch, before the reader has logged in - and nothing rebuilds it in a session. iOS is the one
 * platform that authenticates with the Bearer header rather than the cookie, so an iOS reader who
 * logged in went on sending an empty `authorization` until the app next started cold. [sc-28630]
 *
 * Sits after the retry link so every retry re-reads the store too.
 *
 * Signed out, `authorization` is left off entirely rather than sent empty - the same as
 * axiosModule's interceptor. It is actively removed, not just omitted: the reconnect replay in the
 * error link writes `authorization: ''` into the context when signed out, and spreading the upstream
 * headers would otherwise carry that through. `CSRF-Token` stays as it is; it has its own
 * refresh-and-retry in the error link.
 */
const createAuthLink = () => new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => {
    const next: Record<string, string> = {
      ...headers,
      'CSRF-Token': authStoreGetters.csrfToken as string
    }
    const authorization = authorizationHeader()

    if (authorization) {
      next.authorization = authorization
    } else {
      delete next.authorization
    }

    return { headers: next }
  })

  return forward(operation)
})

const createClient = (): ApolloClient<any> => {
  const retryLink = new RetryLink({
    delay: { initial: 300, max: Infinity, jitter: true },
    attempts: { max: 3, retryIf: retryError => !!retryError }
  })

  return new ApolloClient({
    link: from([
      createErrorLink(),
      retryLink,
      createAuthLink(),
      new HttpLink({
        uri: useConfig().config().apolloClient.url,
        credentials: 'include',
        // Only what cannot change in a session belongs here - see createAuthLink.
        headers: {
          'Referring-App': IsReviewerApp() ? 'reviewer' : 'publisher'
        }
      })
    ]),
    cache: new InMemoryCache({
      /**
       * A MessagePostDto.sender can be a user, an arc team or a pen name, so a bare `id` collides
       * across types — whichever loaded first would win. `modelKey` is "penName_{id}",
       * "arcTeam_{id}" or "user_{id}".
       */
      typePolicies: {
        BaseThreadUserDto: {
          keyFields: ['modelKey']
        }
      }
    }),
    devtools: { enabled: process.env.NODE_ENV === 'development' },
    defaultOptions: {
      query: { fetchPolicy: 'network-only', errorPolicy: 'all' },
      mutate: { errorPolicy: 'all' }
    }
  })
}

/**
 * Raises the global overlay if the operation is still running after 200ms.
 */
const withLoader = <T> (run: () => Promise<T>): Promise<T> => {
  let pending = true

  if (showGlobalLoader && !process.env.SERVER) {
    setTimeout(() => {
      if (pending) {
        Loading.show()
      }
    }, 200)
  }

  return run().finally(() => {
    pending = false
    Loading.hide()
  })
}

export const useApollo = () => {
  /**
   * Built on first use and reused.
   *
   * The old `get client()` constructed a whole new ApolloClient *and a new InMemoryCache* on
   * every access, so the normalised cache never survived a single operation — the global
   * `fetchPolicy: 'network-only'` hid it. The policy is left as it was: turning caching on is a
   * behavioural decision, not part of this refactor.
   */
  const apolloClient = (): ApolloClient<any> => {
    if (!client) {
      client = createClient()
    }

    return client
  }

  const query = <T = BaseModelDto, TVariables extends OperationVariables = OperationVariables> (
    options: QueryOptions<TVariables>
  ): Promise<T> => {
    return withLoader(() => apolloClient().query(options).then(handleResponse))
  }

  const mutate = <T = BaseModelDto, TVariables extends OperationVariables = OperationVariables> (
    options: MutationOptions<T, TVariables>
  ): Promise<T> => {
    return withLoader(() => apolloClient().mutate(options).then(handleResponse))
  }

  const globalLoader = (enable: boolean) => {
    showGlobalLoader = enable

    return useApollo()
  }

  /**
   * Exposed for the temporary ApolloClientService adapter, whose interface declares
   * `showGlobalLoader` as a readable property.
   */
  const isGlobalLoaderEnabled = () => showGlobalLoader

  const reset = () => {
    client = void 0
    showGlobalLoader = true
    failedToConnectNotification = void 0
    stopWaitingForConnection()
  }

  return {
    apolloClient,
    query,
    mutate,
    globalLoader,
    isGlobalLoaderEnabled,
    refreshToken,
    handleResponse,
    reset
  }
}
